// Global imports
import { clsx } from 'clsx'
import { Button, ContentEditor, Heading, Image } from '~/components'

// Types
import type { PortableTextBlock } from '@portabletext/react'
import type { SanityButtonProps, SanityRefWithAlt } from '~/types'

interface FeatureCard {
  image?: SanityRefWithAlt
  eyebrow?: string
  title: string
  content?: PortableTextBlock[]
  button?: SanityButtonProps
}

interface BlockFeatureCardsProps {
  content?: PortableTextBlock[]
  cards?: FeatureCard[]
}

// Main export
export default function BlockFeatureCards({
  content,
  cards,
}: BlockFeatureCardsProps) {
  if (!cards?.length) return null

  return (
    <div className="container">
      {content && (
        <div className="mb-8 max-w-2xl">
          <ContentEditor content={content} />
        </div>
      )}
      <div
        className={clsx('grid grid-cols-1 gap-6 md:grid-cols-2 lg:gap-10', {
          'lg:grid-cols-3': cards.length === 3,
          'lg:grid-cols-4': cards.length >= 4,
        })}
      >
        {cards.map((card, i) => (
          <div
            key={i}
            className="flex flex-col overflow-hidden rounded-2xl bg-white"
          >
            {card.image?.asset && (
              <div className="relative aspect-[12/7]">
                <Image
                  image={card.image}
                  imageSize="half"
                  className="absolute inset-0 size-full object-cover"
                />
              </div>
            )}
            <div className="flex flex-1 flex-col items-center gap-4 p-6 text-center">
              {card.eyebrow && (
                <p className="mb-0 font-bold text-teal-green">{card.eyebrow}</p>
              )}
              {card.title && (
                <Heading
                  className="mb-0"
                  tag="h3"
                  size="h5"
                >
                  {card.title}
                </Heading>
              )}
              {card.content && (
                <div className="[&_p:last-child]:mb-0">
                  <ContentEditor content={card.content} />
                </div>
              )}
              {card.button?.title && (
                <div className="mt-auto">
                  <Button {...card.button} />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
